import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { DetailsDisplay } from '../../Compounds/DetailsDrawer/DetailsDisplay';
import { RecordUnavailable } from '../../Compounds/RecordUnavailable/RecordUnavailable';
import { ComponentSpinner } from '../../Compounds/Loading/ComponentSpinner';
import { Text } from '../../Atoms/Text';
import { CardButton } from '../../Compounds/Buttons/CardButton';
import { useEffect, useState } from 'react';
import { InventoryAsset, PortalUserData, SupportCasesItem } from '../../../types';
import { DrawerActions } from '../../Compounds/DetailsDrawer/DrawerActions/DrawerActions';
import {
  ComponentName,
  DetailsMeta,
  getComponentName,
  getMetaData
} from '../../Compounds/DetailsDrawer/detailsDrawersHelpers';
import { Subscription } from 'rxjs';
import { getInventorySubject, useInventory } from '../../../store/Inventory';
import { getWorkflowOrdersSubject, useWorkflowOrders } from '../../../store/WorkflowOrder';
import { WorkflowOrder } from '../../../Api/Orders/ordersApi';
import { getCaseSubject, useSupportCases } from '../../../store/SupportCase';
import { fetchPriceBenchmarks, getPriceBenchmarkSubject, PriceBenchMark } from '../../../store/PriceBenchMark';
import { getSelectedAccountIdsForUrl, getSelectedAccountsSubject, getUser } from '../../../store/User';

export function DetailsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [backClicked, setBackClicked] = useState(false);
  const [title, setTitle] = useState<string | undefined>('');
  const [componentName, setComponentName] = useState<ComponentName | undefined>(undefined);
  const [data, setData] = useState<
    InventoryAsset | SupportCasesItem | PortalUserData | PriceBenchMark | WorkflowOrder | null
  >(null);
  // The record is looked for in a list, so "not here" is only true once that list has been asked for.
  const [dataSettled, setDataSettled] = useState(false);
  const [accountScope, setAccountScope] = useState<string[]>(getSelectedAccountIdsForUrl());
  const [search] = useSearchParams();
  const detailId = search.get('detailId');
  const { fetchInventory } = useInventory();
  const { fetchCases } = useSupportCases();
  const { fetchWorkflowOrders } = useWorkflowOrders();

  // Settling on an empty scope would call a record absent that was never actually looked for.
  const scopePending = !accountScope.length && !!getUser()?.accounts?.length;

  let subject: Subscription;

  const loadData = async () => {
    switch (componentName) {
      case 'inventory':
        await fetchInventory();
        break;
      case 'workflow-order':
        await fetchWorkflowOrders();
        break;
      case 'support':
        await fetchCases();
        break;
      case 'benchmark':
        await fetchPriceBenchmarks();
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    setData(null);
    setDataSettled(false);
    if (subject) subject.unsubscribe();

    const segment = getComponentName(location?.pathname);
    const { componentName, title }: DetailsMeta = getMetaData(segment);
    setTitle(title);
    setComponentName(componentName);

    switch (componentName) {
      case 'inventory':
        subject = getInventorySubject().subscribe((dataCollection: Array<InventoryAsset>) => getData(dataCollection));
        break;
      case 'workflow-order':
        subject = getWorkflowOrdersSubject().subscribe((dataCollection: Array<WorkflowOrder>) =>
          getData(dataCollection)
        );
        break;
      case 'support':
        subject = getCaseSubject().subscribe((dataCollection: Array<SupportCasesItem>) => getData(dataCollection));
        break;
      case 'benchmark':
        subject = getPriceBenchmarkSubject().subscribe((dataCollection: Array<PriceBenchMark>) =>
          getData(dataCollection)
        );
        break;
      default:
        break;
    }

    return () => {
      if (subject) subject.unsubscribe();
    };
    // The route's type decides which list is read, so it belongs here beside the id read out of it.
  }, [detailId, location?.pathname, backClicked]);

  // A record opened before the account scope arrived would otherwise never be asked for.
  useEffect(() => {
    const accountsSub = getSelectedAccountsSubject().subscribe(() => setAccountScope(getSelectedAccountIdsForUrl()));

    return () => accountsSub.unsubscribe();
  }, []);

  const toggleBackClicked = () => {
    setBackClicked(true);
    navigate(-1);
  };

  const getData = (
    dataCollection: Array<InventoryAsset | SupportCasesItem | PortalUserData | PriceBenchMark | WorkflowOrder>
  ) => {
    if (detailId) {
      const dataItem = dataCollection.find((item) => item?.id == detailId);
      if (dataItem) setData(dataItem);
    }
  };

  useEffect(() => {
    if (!componentName || data) return;
    // Nothing to look for without an addressed record, and a page that asks for none must not hold.
    if (!detailId) {
      setDataSettled(true);
      return;
    }
    if (scopePending) return;
    // Without the flag a load that outlives its record settles the NEXT one before its list arrives.
    let current = true;
    loadData().finally(() => {
      if (current) setDataSettled(true);
    });
    return () => {
      current = false;
    };
    // `data` is a dep because it is also a GUARD above: cleared for a new record, this must re-run.
  }, [detailId, componentName, data, scopePending]);

  const unavailableMessage =
    componentName === 'inventory'
      ? 'The inventory record you are attempting to access is “hidden” from view in the portal or does not exist. Please contact your Admin or view Admin Account Details for more info.'
      : 'This record is not available for this account. It may have been hidden or removed.';

  return (
    <div className="px-12 pt-10">
      {componentName !== undefined ? (
        <>
          <div className="flex items-center p-3 relative">
            <Text
              className="font-semibold text-lg"
              dataTestId="details-view-title">
              {title}
            </Text>
            <div className="ml-auto"></div>
            {data && (
              <DrawerActions
                componentName={componentName}
                data={data}
                setShow={() => false}
              />
            )}
            <CardButton
              text="Back"
              onClick={toggleBackClicked}
              className="h-7 px-2 pl-4 ml-4 border border-grey-500 rounded-lg"
            />
          </div>
          {data ? (
            <DetailsDisplay
              data={data}
              componentName={componentName}
              setShow={() => false}
            />
          ) : dataSettled ? (
            <RecordUnavailable
              message={unavailableMessage}
              dataTestId="record-unavailable"
            />
          ) : (
            <div className="mt-16">
              <ComponentSpinner />
            </div>
          )}
        </>
      ) : (
        <div></div>
      )}
    </div>
  );
}
